import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { notificationsApi } from "@/api/notifications.api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/formatters";

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tz = user?.timezone || "Australia/Adelaide";

  const { data: notifData } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => notificationsApi.getAll(20),
    refetchInterval: 60_000,
  });

  const markAllMutation = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const markOneMutation = useMutation({
    mutationFn: (id: number) => notificationsApi.markRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const unread = notifData?.unread ?? 0;
  const items = notifData?.items ?? [];

  const typeIcon: Record<string, string> = {
    sales_spike: "\u26A1",
    sales_drought: "\u23F3",
    milestone: "\uD83C\uDF89",
    capacity_warning: "\uD83D\uDFE1",
    sold_out: "\uD83C\uDF1F",
    new_review: "\u2B50",
    mission: "\u2705",
    default: "\uD83D\uDD14",
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-1.5 rounded-md hover:bg-white/[0.04] transition-colors"
        title="Notifications"
      >
        <svg className="w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-danger rounded-full text-[8px] font-bold text-white flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 max-h-96 overflow-y-auto rounded-md z-50"
          style={{ background: "#313949", boxShadow: "0 4px 20px rgba(0,0,0,0.5)", border: "1px solid #3b4354" }}>
          <div className="flex items-center justify-between p-3 border-b border-surface-600">
            <span className="text-xs font-bold text-text-primary uppercase tracking-wider">Notifications</span>
            {unread > 0 && (
              <button
                onClick={() => markAllMutation.mutate()}
                className="text-[9px] text-accent-light hover:text-accent"
              >
                Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-xs text-text-muted">No notifications yet</p>
            </div>
          ) : (
            items.slice(0, 10).map((n: any) => (
              <button
                key={n.id}
                onClick={() => { if (!n.read_at) markOneMutation.mutate(n.id); }}
                className={`w-full text-left p-3 border-b border-surface-700/50 hover:bg-white/[0.02] transition-colors ${
                  !n.read_at ? "bg-accent/5" : ""
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-sm mt-0.5">{typeIcon[n.notification_type] ?? typeIcon.default}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-[11px] font-medium leading-tight ${!n.read_at ? "text-text-primary" : "text-text-secondary"}`}>
                      {n.title}
                    </p>
                    {n.body && <p className="text-[10px] text-text-muted mt-0.5 line-clamp-2">{n.body}</p>}
                    <p className="text-[9px] text-text-muted mt-1">
                      {fmtDateTime(n.sent_at, tz)}
                    </p>
                  </div>
                  {!n.read_at && <span className="w-2 h-2 bg-accent rounded-full mt-1 shrink-0" />}
                </div>
              </button>
            ))
          )}
          <button
            onClick={() => { setOpen(false); navigate("/notifications"); }}
            className="w-full p-3 text-center text-[11px] text-accent-light hover:text-accent font-medium hover:bg-white/[0.02] transition-colors"
          >
            See all communications
          </button>
        </div>
      )}
    </div>
  );
}
